// The Guess game screen: the picture, the players' clocks and the microphone. Nothing to tap
// except pause — answers are spoken.
import { remainingAt } from "@shared/guess/clock";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { Btn } from "../../components/Button";
import { Avatar } from "../../components/Avatar";
import { Pause, Play, X } from "../../components/Icon";
import { Sheet } from "../../components/Sheet";
import { fmt, useI18n } from "../../i18n";
import { play } from "../../lib/sfx";
import type { GuessController, Feedback } from "./controller";
import { useGuessDict, type GuessDict } from "./i18n";
import { ClockPill, MicMeter, PictureCard, useFrameNow, useMedia } from "./ui";

export function Duel({ ctl, onQuit }: { ctl: GuessController; onQuit(): void }) {
  const t = useGuessDict();
  const v = useSyncExternalStore(ctl.subscribe, ctl.getView, ctl.getView);
  const { clock } = v;
  const running = clock.phase === "running";
  const nowT = useFrameNow(running);
  const players = ctl.match.players;
  const many = players.length > 2;
  // laptops and TVs: the two clocks stand on either side of the picture
  const side = useMedia("(min-width: 900px) and (min-aspect-ratio: 5/4)") && !many;
  const [confirmQuit, setConfirmQuit] = useState(false);
  const [count, setCount] = useState<number | null>(v.status === "countdown" ? 3 : null);

  // 3-2-1 countdown, then the first picture
  useEffect(() => {
    if (count === null) return;
    if (count === 0) {
      play("go");
      const id = window.setTimeout(() => {
        setCount(null);
        ctl.begin();
      }, 650);
      return () => window.clearTimeout(id);
    }
    play("count");
    const id = window.setTimeout(() => setCount(count - 1), 800);
    return () => window.clearTimeout(id);
  }, [count, ctl]);

  // a beep each second in the last 3 s is too noisy for the microphone; the clock pulses instead
  const activeMs = remainingAt(clock, clock.active, nowT);
  const lastSeconds = running && activeMs <= 10_000;

  const fb = v.feedback;
  const penaltyKey = fb && fb.penalty ? fb.id : null;
  const micLabel = v.mic === "checking" ? t.checking : v.mic === "hearing" ? t.hearing : v.mic === "listening" ? t.listening : "";
  const activeName = players[clock.active]?.name ?? "";

  const clocks = players.map((p, i) => (
    <ClockPill
      tall={side}
      key={i}
      name={p.name}
      avatar={p.avatar}
      ms={remainingAt(clock, i, nowT)}
      active={i === clock.active && clock.phase !== "over"}
      out={clock.players[i].out}
      compact={many}
      penaltyKey={penaltyKey && fb?.player === i ? penaltyKey : null}
      mood={fb && fb.player === i && fb.id === v.feedback?.id ? (fb.kind === "correct" ? "happy" : fb.kind === "wrong" || fb.kind === "out" ? "sad" : undefined) : undefined}
      align={!many && i === 1 ? "right" : "left"}
    />
  ));

  return (
    <div
      className="fixed inset-0 z-[45] flex flex-col overflow-hidden"
      style={{ background: `color-mix(in oklab, ${lastSeconds ? "var(--color-bad)" : "var(--color-guess)"} ${lastSeconds ? 12 : 7}%, var(--color-ground))`, transition: "background .4s" }}
    >
      {/* top bar */}
      <div className="flex items-center justify-between gap-3 px-4 pt-[max(12px,env(safe-area-inset-top))]">
        <button type="button" className="icon-btn" aria-label={t.pausedTitle} onClick={() => ctl.pause("user")} disabled={clock.phase === "over" || clock.phase === "ready"}>
          <Pause size={20} />
        </button>
        <div className="min-w-0 truncate text-center font-extrabold text-ink-2">{ctl.match.category.name}</div>
        <span className="w-11" />
      </div>

      {/* clocks (phones: a row on top) */}
      {!side && <div className={`mx-auto mt-3 grid w-full max-w-[980px] gap-3 px-4 ${many ? "grid-cols-3" : "grid-cols-2"}`}>{clocks}</div>}

      {/* turn */}
      <div className="mt-3 h-7 text-center font-extrabold" aria-live="polite">
        {clock.phase !== "over" && clock.phase !== "ready" && (
          <motion.span key={clock.active} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className={lastSeconds ? "text-bad" : "text-guess"}>
            {lastSeconds ? t.lastSeconds : fmt(t.turnOf, { name: activeName })}
          </motion.span>
        )}
      </div>

      {/* picture (laptops and TVs: between the two clocks) */}
      <div className={`relative flex min-h-0 flex-1 items-center justify-center px-4 py-3 ${side ? "gap-[4vw]" : ""}`}>
        {side && <div className="flex flex-1 justify-end">{clocks[0]}</div>}
        <div className="relative">
          <AnimatePresence mode="popLayout">
            <motion.div
              key={v.picture ? `p${v.picture.i}` : "empty"}
              initial={{ opacity: 0, scale: 0.86, rotate: -2 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 1.06 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
            >
              <PictureCard picture={v.picture} size={side ? "min(44vw, calc(100dvh - 220px), 720px)" : "min(88vw, calc(100dvh - 330px), 620px)"} dim={clock.phase === "paused"} hearing={v.mic === "hearing"} />
            </motion.div>
          </AnimatePresence>
          {v.loading && v.status === "playing" && clock.phase !== "over" && (
            <div className="absolute inset-0 grid place-items-center font-bold text-ink-2">{t.loadingPictures}</div>
          )}
          <FeedbackOverlay fb={fb} t={t} players={players} />
        </div>
        {side && <div className="flex flex-1 justify-start">{clocks[1]}</div>}
      </div>

      {/* microphone + short feedback line */}
      <div className="px-4 pb-[max(16px,env(safe-area-inset-bottom))]">
        <SmallFeedback fb={fb} t={t} />
        <div className="mt-2">
          <MicMeter level={v.level} state={v.mic} label={micLabel} />
        </div>
      </div>

      {/* countdown */}
      <AnimatePresence>
        {count !== null && (
          <motion.div className="absolute inset-0 z-40 grid place-items-center bg-[rgba(26,10,49,.82)]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div
              key={count}
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 1.6, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 18 }}
              className="display text-center text-[min(42vw,260px)] leading-none text-guess"
            >
              {count === 0 ? <span className="text-[min(16vw,120px)]">{t.countdownGo}</span> : count}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* pause / technical pause */}
      <AnimatePresence>
        {clock.phase === "paused" && (
          <motion.div className="absolute inset-0 z-40 grid place-items-center bg-[rgba(26,10,49,.86)] px-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="w-full max-w-sm text-center">
              <h2 className="display text-4xl">{clock.pauseKind === "technical" ? t.techPause : t.pausedTitle}</h2>
              <p className="mt-3 text-lg text-ink-2">{clock.pauseKind === "technical" ? t.techPauseText : t.pausedText}</p>
              <div className="mt-8 grid gap-3">
                <Btn game="guess" size="lg" block icon={<Play size={20} />} onClick={() => ctl.resume()}>
                  {t.resume}
                </Btn>
                <Btn variant="ghost" block icon={<X size={18} />} onClick={() => setConfirmQuit(true)}>
                  {t.quit}
                </Btn>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Sheet open={confirmQuit} onClose={() => setConfirmQuit(false)} size="narrow" title={t.quit}>
        <p className="text-lg text-ink-2">{t.quitConfirm}</p>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <Btn variant="soft" onClick={() => setConfirmQuit(false)}>
            {t.back}
          </Btn>
          <Btn
            onClick={async () => {
              setConfirmQuit(false);
              await ctl.quit();
              onQuit();
            }}
          >
            {t.quit}
          </Btn>
        </div>
      </Sheet>
    </div>
  );
}

export function answerText(answer: { ka: string; en: string } | undefined | null, lang: string) {
  if (!answer) return "";
  return lang === "ka" ? answer.ka || answer.en : answer.en || answer.ka;
}

/** Big, short-lived verdicts over the picture (correct / wrong / skip / out). */
export function FeedbackOverlay({ fb, t, players }: { fb: Feedback | null; t: GuessDict; players: { name: string; avatar: number }[] }) {
  const [visible, setVisible] = useState<Feedback | null>(null);
  useEffect(() => {
    if (!fb || !["correct", "wrong", "skip", "out"].includes(fb.kind)) return;
    setVisible(fb);
    const id = window.setTimeout(() => setVisible((cur) => (cur?.id === fb.id ? null : cur)), fb.kind === "out" ? 1400 : 1050);
    return () => window.clearTimeout(id);
  }, [fb]);
  const { lang } = useI18n();
  const style = useMemo(() => {
    if (!visible) return null;
    switch (visible.kind) {
      case "correct":
        return { bg: "var(--color-good)", ink: "#06240f", title: t.correct };
      case "wrong":
        return { bg: "var(--color-bad)", ink: "#fff", title: t.wrong };
      case "skip":
        return { bg: "var(--color-flame)", ink: "#2a1200", title: t.skip };
      default:
        return { bg: "var(--color-ground-4)", ink: "#fff", title: t.out };
    }
  }, [visible, t]);
  return (
    <AnimatePresence>
      {visible && style && (
        <motion.div
          key={visible.id}
          className="pointer-events-none absolute inset-0 grid place-items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <motion.div
            initial={{ scale: 0.6, rotate: visible.kind === "wrong" ? -4 : 3 }}
            animate={{ scale: 1, rotate: 0, x: visible.kind === "wrong" ? [0, -10, 10, -6, 6, 0] : 0 }}
            transition={{ type: "spring", stiffness: 420, damping: 16 }}
            className="mx-4 max-w-[90%] rounded-[28px] px-6 py-5 text-center"
            style={{ background: style.bg, color: style.ink, boxShadow: "0 8px 0 rgba(0,0,0,.25)" }}
          >
            {visible.kind === "out" && (
              <div className="mb-2 flex justify-center">
                <Avatar index={players[visible.player]?.avatar ?? 0} size={72} mood="sad" disc={false} />
              </div>
            )}
            <div className="display text-4xl sm:text-5xl">{style.title}</div>
            {visible.kind === "out" && <div className="mt-1 text-xl font-extrabold">{players[visible.player]?.name}</div>}
            {(visible.kind === "wrong" || visible.kind === "skip") && <div className="mt-1 text-lg font-extrabold">{t.minus3}</div>}
            {visible.answer && (
              <div className="mt-2 text-xl font-extrabold">{visible.kind === "correct" ? answerText(visible.answer, lang) : fmt(t.itWas, { answer: answerText(visible.answer, lang) })}</div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** One line under the picture: what was heard and why it did not count. */
export function SmallFeedback({ fb, t }: { fb: Feedback | null; t: GuessDict }) {
  const [shown, setShown] = useState<Feedback | null>(null);
  useEffect(() => {
    if (!fb) return;
    setShown(fb);
    const id = window.setTimeout(() => setShown((cur) => (cur?.id === fb.id ? null : cur)), 2600);
    return () => window.clearTimeout(id);
  }, [fb]);
  let text = "";
  let tone = "text-ink-2";
  if (shown) {
    if (shown.kind === "unclear") {
      text = t.unclear;
      tone = "text-flame";
    } else if (shown.kind === "ignored") {
      text = t.ignored;
    } else if (shown.kind === "wrong") {
      const why = shown.reason === "negated" ? t.negated : shown.reason === "multiple" ? t.multiple : shown.reason === "other" ? t.other : "";
      text = [shown.heard ? fmt(t.heard, { text: shown.heard }) : "", why].filter(Boolean).join(" · ");
      tone = "text-bad";
    } else if (shown.kind === "correct" && shown.heard) {
      text = fmt(t.heard, { text: shown.heard });
      tone = "text-good";
    }
  }
  return (
    <div className="h-7 text-center text-base font-bold" aria-live="polite">
      <AnimatePresence mode="wait">
        {text && (
          <motion.p key={shown?.id} className={`truncate ${tone}`} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            {text}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}
